/* I-1 · « campagne principale » on the campaign page (campaign-system review E.4 and § I row I-1, word 1; merchant Version 19
   `campParams` and `A.cPrin`, the state of record). One principale per establishment: the supports that « suivent la
   campagne principale » open it. The card sits in the Paramètres tab, as Version 19 draws it:
   - the principale: Version 19's line, and how to change it (from another campaign's page);
   - en ligne or programmée and not the principale (Version 20): today's principale, how many supports follow it, « Définir
     comme campagne principale » (the owner's, the administrators' and the managers' since the manager row; the API's 403
     forbidden_role says the same), then Version 19's dialog « Changer de campagne principale ? » (a programmée one says
     when its followers move, and what they open until then) and its toast;
   - paused: « Reprenez-la d'abord … » (the API would take it, the app is the narrower one); terminée: never; a brouillon:
     « Seule une campagne en ligne peut être la principale. ».
   The count is the supports at its establishments that follow the principale (no fixed pointer), members' personal QRs
   included, as the pause and end lines count them and as the API's `following` answers. The demo keeps this world's choice
   (lib/campaign setPrincipal); with an API the server decides (lib/campaign-api choosePrincipal) and its answer goes into
   the mirror. A refusal says the app's words (C-2: `principaleRefusal`, never the API's `message`). */
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Modal, useToast } from '@/ui'
import { ApiError } from '@/lib/api'
import { principalOf, type CampaignLite, type SupportRef } from '@/lib/campaign'
import { choosePrincipal, refusalLines } from '@/lib/campaign-api'
import { canBuild } from './roles'

/** Why choosing the principale was refused, in the app's words (C-2, `tillRefusal`'s shape). */
export function principaleRefusal(e: unknown): string {
  if (!(e instanceof ApiError) || e.status === 401) return refusalLines(e)[0]
  switch (e.code) {
    case 'not_principal_eligible': return 'Seule une campagne en ligne peut être la principale. Rechargez la page : celle-ci a changé d’état entre-temps.'
    case 'location_not_covered': return 'Cette campagne ne couvre pas cet établissement. Rechargez la page.'
    case 'forbidden_role': return 'Votre rôle ne permet pas de choisir la campagne principale.'
    case 'not_found': return 'Cette campagne n’existe pas, ou plus, sur le serveur. Rechargez la page.'
  }
  return 'L’action n’a pas abouti : rien n’a changé. Réessayez dans un instant.'
}

/** The supports that follow the principale at these establishments (no fixed pointer), members' QRs included. */
export const followersAt = (sups: SupportRef[], locs: string[]) => sups.filter(s => !s.pinned && locs.includes(s.locationId)).length

/** Is this campaign the principale of one of its establishments (the row's `principal`)? */
export const isPrincipale = (id: string | undefined, lite: CampaignLite[]) => (lite.find(c => c.id === id)?.principal ?? []).length > 0

export function PrincipaleCard({ id, name, lite, covered, supports, onChanged }:
  { id: string; name: string; lite: CampaignLite[]; covered: string[]; supports: SupportRef[]; onChanged: () => void }) {
  const toast = useToast()
  const qc = useQueryClient()
  const [asking, setAsking] = useState(false)
  const [sending, setSending] = useState(false)
  const inFlight = useRef(false)
  const me = lite.find(c => c.id === id)
  const status = me?.status ?? 'draft'
  const mine = (me?.principal ?? []).length > 0
  /* V1 has one establishment: the principale it has today (several is the multi-location backlog) */
  const today = covered.map(l => principalOf(l, lite)).find(Boolean) ?? null
  const n = followersAt(supports, covered)
  /* the manager row (handover answer 3): the owner, the administrators and a manager choose; a Collaborateur reads the card */
  const canChoose = canBuild()
  /* Version 20 (wireframe-set-vet « Re-vet · Version 20 », point 1): a programmée campaign can be chosen; its followers move at
     its start, and until then they open today's rule, the first live campaign. A paused one is resumed first (the app is the
     narrower one here); a terminée never. */
  const offered = status === 'active' || status === 'scheduled'
  const until = status === 'scheduled' ? lite.filter(c => c.status === 'active' && c.id !== id && c.locations.some(l => covered.includes(l))).sort((a, b) => a.seq - b.seq)[0] ?? null : null
  const startDay = me && /^\d{4}-\d{2}-\d{2}/.test(me.startAt) ? new Date(me.startAt.slice(0, 10) + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) : null

  const choose = async () => {
    if (inFlight.current || !me) return
    inFlight.current = true; setSending(true)
    try {
      const r = await choosePrincipal(me, supports, lite)
      if (!r) { toast({ message: principaleRefusal(new ApiError(409, 'not_principal_eligible', '')) }); return }
      setAsking(false)
      void qc.invalidateQueries({ queryKey: ['supports'] })
      onChanged()
      toast({ message: `« ${name} » est votre campagne principale.` })
    } catch (e) { toast({ message: principaleRefusal(e) }) }
    finally { inFlight.current = false; setSending(false) }
  }

  const follow = n === 0 ? 'Aucun support ne suit la principale.' : n === 1 ? '1 support suit la principale.' : `${n} supports suivent la principale.`
  const when = status === 'scheduled'
    ? ` dès qu’elle sera en ligne${startDay ? `, le ${startDay}` : ''}.${until && today && until.id === today.id ? '' : until ? ` D’ici là, ${n === 1 ? 'il ouvre' : 'ils ouvrent'} « ${until.name} ».` : ` D’ici là, ${n === 1 ? 'il affiche' : 'ils affichent'} « momentanément suspendu ».`}`
    : ' dès maintenant.'
  const move = n === 0 ? 'Aucun support ne suit la principale aujourd’hui.'
    : `${n === 1 ? '1 support passera' : `${n} supports passeront`} ${today ? `de « ${today.name} » ` : ''}à « ${name} »${when} ${n === 1 ? 'Son QR ne change pas.' : 'Leurs QR ne changent pas.'}`

  return (
    <section className="sect" data-testid="principale">
      <p className="t-eyebrow">Campagne principale</p>
      {mine ? (<>
        <p className="t-body" style={{ margin: 0 }}>{status === 'active'
          ? 'C’est votre campagne principale. Les supports qui suivent la principale l’ouvrent.'
          : 'C’est votre campagne principale : les supports qui la suivent l’ouvriront dès qu’elle sera en ligne.'}</p>
        <p className="f-help" style={{ margin: 0 }}>Pour en changer, ouvrez une autre campagne en ligne et choisissez « Définir comme campagne principale ».</p>
      </>) : offered ? (<>
        <p className="f-help" style={{ margin: 0 }}>{today ? `Aujourd’hui : « ${today.name} ».` : 'Aucune campagne principale.'} {follow}</p>
        {canChoose && <div><Button size="sm" onClick={() => setAsking(true)}>Définir comme campagne principale</Button></div>}
      </>) : (
        <p className="f-help" style={{ margin: 0 }}>{status === 'paused' ? 'Reprenez-la d’abord pour en faire la campagne principale.'
          : status === 'ended' || status === 'archived' ? 'Une campagne terminée ne peut pas être la principale.'
          : 'Seule une campagne en ligne peut être la principale.'}</p>
      )}
      <Modal open={asking} title="Changer de campagne principale ?" onClose={() => setAsking(false)}
        actions={<>
          <Button size="sm" variant="quiet" onClick={() => setAsking(false)}>Annuler</Button>
          <Button size="sm" variant="primary" disabled={sending} onClick={() => { void choose() }}>Définir comme principale</Button>
        </>}>
        <p className="t-body" data-testid="principale-effet">{move} Les supports qui pointent vers une campagne précise ne bougent pas.</p>
      </Modal>
    </section>
  )
}
